import _ from 'lodash';
import React, { Component } from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Modal, Tabs, Tab, FormCheck } from 'react-bootstrap';
import ReactTable from 'react-table';

import modal from '../../typings/Modal';
import Model from '../../model/ShopKeeper.model';
import LoadingPage from '../../../LoadingPage';
import { ICustomerCoreBankItem } from '../../typings/CustomerHistory';

/* The invoice history modal displays a tabbed interface of both the customer's
   invoice history (legacy option H) as well as the cutomer's core bank (legacy
   option K).

   Currently, only 1 invoice may be credited at a time, per DBPIP/QM limitations, but
   multiple items on the same invoice may be credited in one pass (including partially
   if qty > 1).

   Clicking on the invoice number within invoice history (H) or via the core bank (K)
   will launch the InvoiceCreditModal.  In the latter case, the selected core will be
   auto checked for credit when the invoice loads.

   Credit invoices are optionally shown (not selected by default).

   This modal waits for the customer's invoice history to load first, but does not
   wait for core return days data to load. The point is to spread the async data load
   out where possible to make the UI as snappy as possible.  Some clients have very large
   core return data sets.
*/

interface IProps {
  container: Object;
  hide: () => void;
  model: Model;
  show: boolean;
}

// We want case insensitive and searching within strings
const filterOverride = ({ id, value }: { id: string; value: string }, row: string[]) => {
  return row[id] ? row[id].toLowerCase().includes(value.toLowerCase()) : false;
};

@observer
class CustomerHistoryModal extends Component<IProps, {}> {
  @observable activeTab = '1';
  @observable showCreditInvoices = true;
  @observable partSearchInput = '';
  partSearchDebounced: (evt: any) => void;

  constructor(props: IProps) {
    super(props);
    this.partSearchDebounced = _.debounce(this.partSearch, 500);
  }

  partSearch = (partSearchInput: string) => {
    // Scroll our tables to top on global part search (prevents ostensibly blank result)
    const reactTables = document.querySelector('.sk-customerHistoryModal')!.querySelectorAll('.rt-tbody');
    reactTables[0].scrollTop = 0; // invoice history, will exist if part search is available

    // core bank - only exists if customer has entries
    if (reactTables[1]) {
      reactTables[1].scrollTop = 0;
    }

    this.partSearchInput = partSearchInput.toUpperCase();
  };

  render() {
    const { container, hide, model, show } = this.props;
    let invoiceSummaryData: Array<{
      invoiceNum: string;
      invoiceDate: string;
      invoiceDateTime: string;
      invoiceTotal: number;
      poNum: string;
      creditInvoice: boolean;
    }> = [];
    let coreBank: Array<ICustomerCoreBankItem & { invoiceDateTime: string }> = [];

    // Filter as necessary (part search, etc.)
    if (model.customerHistoryLazyLoaded) {
      invoiceSummaryData = Object.keys(model.customerHistory.invoiceData)
        .filter(invoiceNum => {
          const invoice = model.customerHistory.invoiceData[invoiceNum];
          let filterPass = true;

          // Show/Hide Credit Invoices Filter
          if (!this.showCreditInvoices && invoice.creditInvoice) {
            filterPass = false;
          }

          // Part Number Search Filter
          if (
            !!this.partSearchInput &&
            !~_.findIndex(
              invoice.partLines,
              o => typeof o.partNum === 'string' && o.partNum.includes(this.partSearchInput)
            )
          ) {
            filterPass = false;
          }

          return filterPass;
        })
        .map(invoiceNum => {
          const { invoiceDate, invoiceTime, invoiceTotal, poNum, creditInvoice } =
            model.customerHistory.invoiceData[invoiceNum];
          return {
            invoiceNum,
            invoiceDate,
            invoiceDateTime: `${invoiceDate} ${invoiceTime}`, // better sorting
            invoiceTotal,
            poNum,
            creditInvoice,
          };
        });

      coreBank = model.customerHistory.coreBank
        .filter(
          item =>
            !(
              !!this.partSearchInput &&
              typeof item.partNum === 'string' &&
              !item.partNum.includes(this.partSearchInput)
            )
        )
        .map(item => ({
          ...item,
          invoiceDateTime: `${item.invoiceDate} ${_.get(item, 'invoiceTime', '') || '00:00:00'}`,
        }));
    }

    return (
      <Modal
        backdrop="static"
        size="lg"
        container={container as any}
        dialogClassName="sk-customerHistoryModal"
        onHide={hide as any}
        show={show}
      >
        <Modal.Header>
          <Modal.Title>Invoice History / Core Bank</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {model.customerHistoryLazyLoaded ? (
            Object.keys(model.customerHistory.invoiceData).length > 0 ? (
              <>
                <div className="sk-customerHistoryFilterControls">
                  <div>
                    <FormCheck>
                      <FormCheck.Input
                        type="checkbox"
                        className="sk-useMaterialInput"
                        id="show-credit-invoices"
                        onChange={() => (this.showCreditInvoices = !this.showCreditInvoices)}
                        defaultChecked={this.showCreditInvoices}
                      />
                      <FormCheck.Label htmlFor="show-credit-invoices">Show Credit Invoices</FormCheck.Label>
                    </FormCheck>
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Search by Part #"
                      onChange={evt => this.partSearchDebounced(evt.target.value)}
                    />
                  </div>
                </div>
                <Tabs
                  defaultActiveKey={this.activeTab}
                  id="sk-customerHistory"
                  onSelect={(eventKey: string | null) => {
                    if (eventKey) {
                      this.activeTab = eventKey;
                    }
                  }}
                >
                  <Tab eventKey="1" title="Invoice History">
                    <br />
                    <div style={{ height: '485px' }}>
                      <ReactTable
                        className="-striped"
                        data={invoiceSummaryData}
                        defaultFilterMethod={filterOverride}
                        defaultPageSize={invoiceSummaryData.length > 500 ? 10 : invoiceSummaryData.length}
                        defaultSorted={[
                          {
                            desc: true,
                            id: 'invoiceDateTime',
                          },
                        ]}
                        getTrProps={(state: any, rowInfo: any) => {
                          if (rowInfo) {
                            return {
                              style: {
                                background: _.get(rowInfo, 'original.creditInvoice', false) ? '#fee' : undefined,
                              },
                            };
                          }
                          return {};
                        }}
                        showPagination={invoiceSummaryData.length > 500}
                        style={{
                          maxHeight: '470px',
                        }}
                        columns={[
                          {
                            Cell: props => (
                              <span
                                className="sk-actionLink"
                                onClick={() => {
                                  model.loadCreditInvoice(props.value, this.partSearchInput);
                                }}
                              >
                                {props.value}
                              </span>
                            ),
                            Header: 'Invoice',
                            accessor: 'invoiceNum',
                            filterable: true,
                            style: {
                              textAlign: 'center',
                            },
                          },
                          {
                            Header: () => <span title="View Invoice Print">View</span>,
                            Cell: props => (
                              <span
                                className="sk-actionLink"
                                onClick={() => {
                                  modal.open('viewInvoice', { lookupKey: props.original.invoiceNum });
                                }}
                              >
                                View / Print
                              </span>
                            ),
                            style: {
                              textAlign: 'center',
                            },
                          },
                          {
                            Cell: props => props.original.invoiceDate,
                            Header: () => <span title="Invoice Date">Date</span>,
                            accessor: 'invoiceDateTime',
                            style: {
                              textAlign: 'center',
                            },
                          },
                          {
                            Cell: props => props.value.toFixed(2),
                            Header: 'Total',
                            accessor: 'invoiceTotal',
                            style: {
                              textAlign: 'right',
                            },
                          },
                          {
                            Header: 'Customer PO',
                            accessor: 'poNum',
                            filterable: true,
                            style: {
                              textAlign: 'center',
                            },
                          },
                        ]}
                      />
                    </div>
                  </Tab>
                  <Tab eventKey="2" title="Core Bank">
                    {model.customerHistory.coreBank.length > 0 ? (
                      <>
                        <br />
                        <div style={{ height: '485px' }}>
                          <ReactTable
                            className="-striped"
                            data={coreBank}
                            defaultFilterMethod={filterOverride}
                            defaultPageSize={coreBank.length}
                            defaultSorted={[
                              {
                                desc: true,
                                id: 'invoiceDateTime',
                              },
                            ]}
                            showPagination={false}
                            style={{
                              maxHeight: '470px',
                            }}
                            columns={[
                              {
                                Header: 'Qty',
                                accessor: 'qty',
                                style: {
                                  textAlign: 'center',
                                },
                                width: 50,
                              },
                              {
                                Cell: props => <span title={props.value}>{props.value}</span>,
                                Header: 'Part',
                                accessor: 'partNum',
                                filterable: true,
                                style: {
                                  textAlign: 'center',
                                },
                              },
                              {
                                Cell: props => <span title={props.value}>{props.value}</span>,
                                Header: 'Description',
                                accessor: 'desc',
                                filterable: true,
                                style: {
                                  textAlign: 'center',
                                },
                              },
                              {
                                Cell: props => props.original.invoiceDate,
                                Header: () => <span title="Invoice Date">Date</span>,
                                accessor: 'invoiceDateTime',
                                style: {
                                  textAlign: 'center',
                                },
                                width: 90,
                              },
                              {
                                Cell: props => (
                                  <span
                                    className="sk-actionLink"
                                    onClick={() => {
                                      model.loadCreditInvoice(props.value, props.original.partNum);
                                    }}
                                  >
                                    {props.value}
                                  </span>
                                ),
                                Header: 'Invoice',
                                accessor: 'invoiceNum',
                                filterable: true,
                                style: {
                                  textAlign: 'center',
                                },
                              },
                              {
                                Header: 'Customer PO',
                                accessor: 'poNum',
                                filterable: true,
                                style: {
                                  textAlign: 'center',
                                },
                              },
                              {
                                Cell: props => <span title={props.value}>{props.value}</span>,
                                Header: 'Serial #s',
                                accessor: 'serials',
                                filterable: true,
                                style: {
                                  textAlign: 'center',
                                },
                              },
                            ]}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="sk-emptyCoreBank">Core Bank Search Empty</div>
                    )}
                  </Tab>
                </Tabs>
              </>
            ) : (
              <div style={{ marginTop: '15px' }}>No invoice history found for this customer.</div>
            )
          ) : (
            <LoadingPage />
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="sk-customerHistoryModalFooter">
            <div>
              {model.customerHistoryLazyLoaded ? (
                <>
                  <Button
                    onClick={() => {
                      model.loadCustomerHistory();
                    }}
                    variant="secondary"
                  >
                    <i className="zmdi zmdi-refresh" /> Refresh
                  </Button>
                  <Button
                    onClick={() => {
                      model.loadCustomerHistory(true);
                    }}
                    variant="secondary"
                  >
                    <i className="zmdi zmdi-refresh" /> Full History (Slower)
                  </Button>
                </>
              ) : null}
            </div>
            <div>
              <Button variant="secondary" onClick={() => hide()}>
                Close
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default CustomerHistoryModal;
